import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import useAuthStore from 'store/auth';
import Icon from 'components/icon/Icon';
import { MILESTONES } from 'lib/constants';
import { cn } from 'lib/utils';
import FamilyMembers from 'components/FamilyMembers';
import { PATH_ROUTES } from 'routes/path';
import SettingsSheet from './SettingsSheet';
import useSidebar from 'hooks/useSidebar';
import { NavItem } from '../../types';
import useLogout from 'hooks/useLogout';
import Spinner from 'components/Spinner';

import MedsiCheckFull from 'assets/medsi-check-full.png';
import MedsiCheckDark from 'assets/medsi-check-dark.png';
import { ScrollArea } from 'components/ui/scroll-area';

export default function MobileNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const userEmail = useAuthStore((store) => store.userEmail);
  const userAttributes = useAuthStore((store) => store.userAttributes);
  const accountStatus = useAuthStore((store) => store.accountStatus);
  const { upperNav, lowerNav } = useSidebar();

  const [activeNav, setActiveNav] = useState('Dashboard');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openSettingSheet, setOpenSettingSheet] = useState<boolean>(false);

  const onSettingclick = () => setOpenSettingSheet(true);
  const { onLogout, isLoggingOut } = useLogout();

  const UPPER_NAV = upperNav();
  const LOWER_NAV = lowerNav(onLogout, onSettingclick);

  useEffect(() => {
    const navs = UPPER_NAV.concat(LOWER_NAV);
    const navName = navs.find((nav) => nav.href === pathname)?.name;
    setActiveNav(navName || 'Dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const disabled = useMemo(
    () =>
      [MILESTONES.TNC, MILESTONES.USER_DETAIL].includes(
        currentMilestone as MILESTONES
      ),
    [currentMilestone]
  );

  const onViewProfile = () => {
    navigate(PATH_ROUTES.PROFILE);
    setSheetOpen(false);
  };

  const isGenerating = useCallback(
    () =>
      [
        PATH_ROUTES.GENERATE_AI_REPORT,
        PATH_ROUTES.GENERATE_LAB_REPORT,
        PATH_ROUTES.START_FACE_SCAN,
      ].includes(pathname),
    [pathname]
  );

  return (
    <>
      <Spinner isSpinning={isLoggingOut} />

      <div className='flex items-center justify-between h-14 lg:hidden bg-primary-dark'>
        <div className='sm:border-r-[3px] h-full grid place-items-center'>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant='outline'
                className='bg-transparent border-none shrink-0 lg:hidden '
              >
                <Menu className='w-5 h-5 text-white' />
                <span className='sr-only'>Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side='left'
              hideClose
              className='p-0 sm:p-0 lg:hidden font-isidora'
            >
              <ScrollArea className='h-screen'>
                <div className='flex flex-col p-4'>
                  <nav className='flex flex-col justify-center h-full space-y-4'>
                    <div className='flex justify-center mb-2'>
                      <div className='h-6 sm:h-8 w-fit'>
                        <img
                          src={MedsiCheckDark}
                          alt='medsicheck-logo'
                          className='object-cover w-full h-full'
                        />
                      </div>
                    </div>

                    <div className='w-full px-4 py-5 space-y-2 rounded-2xl bg-primary-dark'>
                      <div className='leading-none text-white'>
                        <h1 className='font-semibold'>
                          {userAttributes?.given_name
                            ? `${userAttributes?.given_name} ${userAttributes?.family_name}`
                            : 'N/A'}
                        </h1>
                        <p className='text-sm'>{userEmail}</p>
                      </div>

                      <Button
                        className='text-sm h-7 bg-secondary'
                        onClick={onViewProfile}
                        disabled={!accountStatus?.approved}
                      >
                        View Profile
                      </Button>
                    </div>

                    <div className='flex flex-col justify-between flex-1 h-full gap-12'>
                      <div className='flex flex-col w-full'>
                        {UPPER_NAV.map((nav) => (
                          <NavButton
                            key={nav.name}
                            {...nav}
                            onClick={() => {
                              if (nav?.href) {
                                navigate(nav.href);
                                setSheetOpen(false);
                              }
                            }}
                            disabled={
                              disabled ||
                              (nav.href !== PATH_ROUTES.DASHBOARD &&
                                !accountStatus?.approved)
                            }
                            isActive={activeNav === nav.name}
                          />
                        ))}
                      </div>

                      <div className='flex flex-col w-full'>
                        {LOWER_NAV.map((nav) => {
                          return (
                            <NavButton
                              key={nav.name}
                              {...nav}
                              onClick={() => {
                                if (nav?.href) {
                                  navigate(nav.href);
                                  setSheetOpen(false);
                                }
                                nav.onClick?.();
                              }}
                              disabled={
                                (disabled && nav.icon !== 'logout') ||
                                (!accountStatus?.approved &&
                                  nav.icon === 'settings')
                              }
                              isActive={activeNav === nav.name}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </nav>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>

        <div className='h-6 w-fit'>
          <img
            src={MedsiCheckFull}
            alt='medsicheck'
            className='object-cover w-full h-full'
          />
        </div>

        {isGenerating() ? (
          <div className='mr-2 sm:mr-4 pl-7' />
        ) : (
          <div
            className={cn(
              'sm:border-l-[3px] mr-2 sm:mr-4 shrink-0 h-full pl-7 grid place-items-center',
              !accountStatus?.approved && 'border-l-0'
            )}
          >
            <FamilyMembers className='size-7' />
          </div>
        )}
      </div>

      <SettingsSheet
        open={openSettingSheet}
        onClose={() => setOpenSettingSheet(false)}
        onSidebarClose={() => setSheetOpen(false)}
      />
    </>
  );
}

type NavButtonProps = NavItem & { disabled: boolean; isActive: boolean };
function NavButton(props: NavButtonProps) {
  const { icon, name, size, onClick, disabled, isActive } = props;
  return (
    <Button
      id={icon}
      variant='ghost'
      className='justify-start w-full px-0 hover:bg-transparent gap-x-2'
      onClick={onClick}
      disabled={disabled}
    >
      <Icon
        icon={icon}
        size={size ?? 20}
        color={disabled ? '#000' : isActive ? '#10A4B1' : '#000'}
      />
      <span
        className={cn(
          disabled
            ? 'text-gray-900'
            : isActive
            ? 'text-primary'
            : 'text-gray-900'
        )}
      >
        {name}
      </span>
    </Button>
  );
}
